import { loadVideoJs } from '../../../utils/videojs-loader';
import { Component, createRef } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { Subscription } from 'rxjs';

import { BrowserUtils } from '../../../ui-utils/browser-utils';
import { RxUtils } from '../../../utils/rx-utils';
import styles from './media-preview-modal.component.module.scss';

declare var pdfjsLib;

// Supported preview types
const PREVIEWABLE_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico', 'avif'];
const PREVIEWABLE_VIDEO_EXTENSIONS = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'];
const PREVIEWABLE_AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a', 'wma'];
const PREVIEWABLE_DOCUMENT_EXTENSIONS = ['pdf'];
const PREVIEWABLE_TEXT_EXTENSIONS = ['txt', 'json', 'xml', 'csv', 'md', 'log'];

export type MediaType = 'image' | 'video' | 'audio' | 'pdf' | 'text' | 'unknown';

export function getMediaTypeFromExtension(filename: string): MediaType {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    if (PREVIEWABLE_IMAGE_EXTENSIONS.includes(ext)) return 'image';
    if (PREVIEWABLE_VIDEO_EXTENSIONS.includes(ext)) return 'video';
    if (PREVIEWABLE_AUDIO_EXTENSIONS.includes(ext)) return 'audio';
    if (PREVIEWABLE_DOCUMENT_EXTENSIONS.includes(ext)) return 'pdf';
    if (PREVIEWABLE_TEXT_EXTENSIONS.includes(ext)) return 'text';
    return 'unknown';
}

export function isPreviewable(filename: string): boolean {
    return getMediaTypeFromExtension(filename) !== 'unknown';
}

interface MediaPreviewModalComponentProps {
    url: string;
    mediaType: MediaType;
    filename?: string;
    /**
     * Shown above the media. Used when what is on screen is not the file itself -- a
     * server-rendered still standing in for a format this browser cannot decode. Without it the
     * user sees a frozen frame of their video and reasonably concludes the conversion broke.
     */
    notice?: string;
    /**
     * A server-rendered still to show if `url` will not render here.
     *
     * No capability table is right on every browser, OS and codec combination, so this is what
     * makes being wrong survivable: the real file is attempted, and a genuine failure lands on
     * this instead of an error. `fallbackNotice` explains the swap -- silently showing a still
     * of someone's video reads as a broken conversion.
     */
    fallbackUrl?: string;
    fallbackNotice?: string;
    /**
     * What the Download button fetches. Defaults to `url`, and must be set whenever `url` is a
     * poster: handing someone a 200px JPEG of their video because that is what happened to be on
     * screen would be a genuinely damaging surprise.
     */
    downloadUrl?: string;
    onClose?: () => void;
}

interface MediaPreviewModalComponentState {
    /** True once the real file failed and the poster took over. */
    usingFallback: boolean;
    /** video.js factory arrived; the enhanced player can mount. */
    vjsReady: boolean;
    /** The video.js chunk failed to load: degrade to the native elements. */
    useNativePlayer: boolean;
    isLoading: boolean;
    error: string | null;
    currentPage: number;
    totalPages: number;
    scale: number;
    textContent: string | null;
    isVisible: boolean;
    isEditingPage: boolean;
    pageInputValue: string;
}

export class MediaPreviewModalComponent extends Component<MediaPreviewModalComponentProps, MediaPreviewModalComponentState> {
    private canvasRef = createRef<HTMLCanvasElement>();
    private containerRef = createRef<HTMLDivElement>();
    private contentRef = createRef<HTMLDivElement>();
    private pageInputRef = createRef<HTMLInputElement>();
    private portalElement: HTMLDivElement | null = null;
    private root: Root | null = null;
    private pdfDoc: any = null;
    private renderTask: any = null;
    private sub: Subscription;

    readonly state: Readonly<MediaPreviewModalComponentState> = {
        usingFallback: false,
        vjsReady: false,
        useNativePlayer: false,
        isLoading: true,
        error: null,
        currentPage: 1,
        totalPages: 0,
        scale: 1.5,
        textContent: null,
        isVisible: true,
        isEditingPage: false,
        pageInputValue: '1',
    };

    /**
     * Static method to show the modal - creates a portal and renders the modal
     */
    static show(props: Omit<MediaPreviewModalComponentProps, 'onClose'>) {
        if (!BrowserUtils.hasDocument()) return;

        const portalElement = document.createElement('div');
        portalElement.className = 'media-preview-portal';
        document.body.appendChild(portalElement);

        const root = createRoot(portalElement);
        const handleClose = () => {
            root.unmount();
            portalElement.remove();
        };

        root.render(<MediaPreviewModalComponent {...props} onClose={handleClose} />);
    };

    componentDidMount() {
        if (BrowserUtils.hasWindow()) {
            document.addEventListener('keydown', this.handleKeyDown);
            document.body.style.overflow = 'hidden';
        }

        if (this.props.mediaType === 'pdf') {
            this.loadPdfJs().then(() => this.loadPdf());
        } else if (this.props.mediaType === 'text') {
            this.loadTextContent();
        } else if (this.props.mediaType === 'video' || this.props.mediaType === 'audio') {
            // Same on-demand pattern as pdf.js above: the chunk downloads only once a
            // preview opens, so no page bundle carries the player. A failed load is
            // survivable by design — the native <video>/<audio> path below is kept
            // as the degraded experience.
            loadVideoJs()
                .then(vjs => { this.vjsLib = vjs; this.setState({ vjsReady: true }); })
                .catch(() => this.setState({ useNativePlayer: true }));
        } else {
            this.setState({ isLoading: false });
        }
    }

    private vjsLib: any = null;
    private vjsPlayer: any = null;
    private vjsHostRef = createRef<HTMLDivElement>();

    componentDidUpdate() {
        this.maybeInitVjs();
    }

    /**
     * Mount video.js into the React-owned host div (the documented React
     * integration: React renders a stable wrapper, video.js owns the media
     * element inside it, dispose removes only what video.js created).
     */
    private maybeInitVjs() {
        const { mediaType } = this.props;
        if (this.vjsPlayer || !this.vjsLib) return;
        if (mediaType !== 'video' && mediaType !== 'audio') return;
        if (this.state.usingFallback || this.state.useNativePlayer || this.state.error) return;
        const host = this.vjsHostRef.current;
        if (!host) return;

        const el = document.createElement(mediaType === 'audio' ? 'audio' : 'video');
        el.className = 'video-js vjs-big-play-centered';
        if (mediaType === 'video') el.setAttribute('playsinline', '');
        host.appendChild(el);

        const player = this.vjsLib(el, {
            controls: true,
            autoplay: false,
            preload: 'metadata',
            fluid: mediaType === 'video',
            audioOnlyMode: mediaType === 'audio',
            playbackRates: [0.5, 1, 1.25, 1.5, 2],
            sources: [{ src: this.activeUrl }],
        });
        this.vjsPlayer = player;

        player.on('loadedmetadata', () => {
            // Same undecodable-video heuristic as the native path: a decoder with no
            // output paints nothing and never errors. Audio is exempt — it is 0x0 by
            // nature, which is exactly the bug this modal shipped with once before.
            if (mediaType === 'video' && player.videoWidth() === 0 && player.videoHeight() === 0) {
                this.disposeVjs();
                this.failOver('This browser can\'t play this video format.');
                return;
            }
            this.setState({ isLoading: false });
        });
        player.on('error', () => {
            this.disposeVjs();
            if (mediaType === 'video') {
                this.failOver('This browser can\'t play this video format.');
            } else {
                this.setState({ error: 'Failed to load audio', isLoading: false });
            }
        });
    }

    private disposeVjs() {
        if (this.vjsPlayer) {
            // Pause first so audio stops the instant the modal goes, even if
            // dispose's own teardown is ever delayed by a pending source load.
            try { this.vjsPlayer.pause(); } catch (e) { /* not started */ }
            try { this.vjsPlayer.dispose(); } catch (e) { /* already torn down */ }
            this.vjsPlayer = null;
        }
    }

    componentWillUnmount() {
        this.disposeVjs();
        if (BrowserUtils.hasWindow()) {
            document.removeEventListener('keydown', this.handleKeyDown);
            document.body.style.overflow = '';
        }
        RxUtils.safeCloseSubscriber(this.sub);
        if (this.renderTask) {
            this.renderTask.cancel();
        }
    }

    private handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            this.close();
        } else if (this.props.mediaType === 'pdf') {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                this.prevPage();
            } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                this.nextPage();
            }
        }
    };

    private async loadPdfJs(): Promise<void> {
        if (window['pdfjsLib']) {
            // Ensure worker is configured
            pdfjsLib.GlobalWorkerOptions.workerSrc = '/js/pdf.worker.min.js';
            return;
        }

        await Promise.all([
            this.loadScript('/js/pdf.min.js'),
        ]);

        // Configure worker after loading
        if (window['pdfjsLib']) {
            pdfjsLib.GlobalWorkerOptions.workerSrc = '/js/pdf.worker.min.js';
        }
    }

    private loadScript(src: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = reject;
            document.head.appendChild(script);
        });
    }

    private async loadPdf() {
        try {
            this.pdfDoc = await pdfjsLib.getDocument({ url: this.props.url }).promise;
            this.setState({
                totalPages: this.pdfDoc.numPages,
                isLoading: false,
            }, () => {
                // Wait for DOM to fully render and layout to calculate
                // Using setTimeout to ensure flex layout has computed dimensions
                setTimeout(() => {
                    this.renderPage(1);
                }, 100);
            });
        } catch (error) {
            this.setState({
                error: 'Failed to load PDF document',
                isLoading: false,
            });
        }
    }

    private async renderPage(pageNum: number) {
        if (!this.pdfDoc) return;

        // Canvas might not be in DOM yet after loading state change, retry if needed
        if (!this.canvasRef.current) {
            setTimeout(() => this.renderPage(pageNum), 50);
            return;
        }

        try {
            const page = await this.pdfDoc.getPage(pageNum);
            const canvas = this.canvasRef.current;
            const ctx = canvas.getContext('2d');

            // Calculate scale to fit container
            const contentArea = this.contentRef.current;
            const containerWidth = contentArea ? Math.max(contentArea.getBoundingClientRect().width - 48, 400) : Math.min(window.innerWidth * 0.85, 1200);
            const containerHeight = contentArea ? Math.max(contentArea.getBoundingClientRect().height - 48, 300) : Math.min(window.innerHeight * 0.75, 800);

            const baseViewport = page.getViewport(1);
            const scale = Math.min(containerWidth / baseViewport.width, containerHeight / baseViewport.height, 2.5);
            const scaledViewport = page.getViewport(scale);

            canvas.width = scaledViewport.width;
            canvas.height = scaledViewport.height;
            canvas.style.width = '';
            canvas.style.height = '';

            if (this.renderTask) {
                this.renderTask.cancel();
            }

            this.renderTask = page.render({
                canvasContext: ctx,
                viewport: scaledViewport,
            });

            await this.renderTask.promise;
            this.setState({ currentPage: pageNum });
        } catch (error) {
            if (error?.name !== 'RenderingCancelledException') {
            }
        }
    }

    private async loadTextContent() {
        try {
            const response = await fetch(this.props.url);
            const text = await response.text();
            this.setState({
                textContent: text,
                isLoading: false,
            });
        } catch (error) {
            this.setState({
                error: 'Failed to load text content',
                isLoading: false,
            });
        }
    }

    private prevPage = () => {
        if (this.state.currentPage > 1) {
            this.renderPage(this.state.currentPage - 1);
        }
    };

    private nextPage = () => {
        if (this.state.currentPage < this.state.totalPages) {
            this.renderPage(this.state.currentPage + 1);
        }
    };

    private goToPage = (pageNum: number) => {
        if (pageNum >= 1 && pageNum <= this.state.totalPages) {
            this.renderPage(pageNum);
        }
    };

    private handlePageClick = () => {
        this.setState({
            isEditingPage: true,
            pageInputValue: String(this.state.currentPage),
        }, () => {
            this.pageInputRef.current?.focus();
            this.pageInputRef.current?.select();
        });
    };

    private handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ pageInputValue: e.target.value });
    };

    private handlePageInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            const pageNum = parseInt(this.state.pageInputValue, 10);
            if (!isNaN(pageNum)) {
                this.goToPage(pageNum);
            }
            this.setState({ isEditingPage: false });
        } else if (e.key === 'Escape') {
            this.setState({ isEditingPage: false });
        }
        e.stopPropagation();
    };

    private handlePageInputBlur = () => {
        const pageNum = parseInt(this.state.pageInputValue, 10);
        if (!isNaN(pageNum)) {
            this.goToPage(pageNum);
        }
        this.setState({ isEditingPage: false });
    };

    private close = (e?: React.MouseEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (this.props.onClose) {
            this.props.onClose();
        }
    };

    private handleDownload = (e: React.MouseEvent) => {
        e.stopPropagation();
        // Always the real file, never the poster the user might be looking at.
        window.open(this.props.downloadUrl || this.props.url, '_blank');
    };

    /** The source currently on screen: the real file, or the poster that replaced it. */
    private get activeUrl(): string {
        return this.state.usingFallback && this.props.fallbackUrl
            ? this.props.fallbackUrl
            : this.props.url;
    }

    private get activeNotice(): string | undefined {
        return this.state.usingFallback ? this.props.fallbackNotice : this.props.notice;
    }

    /**
     * The real file will not render here. Swap to the poster if there is one; otherwise say so
     * plainly, naming the file rather than blaming the conversion — the file converted fine,
     * this browser just cannot display it.
     */
    private failOver = (reason: string) => {
        if (this.props.fallbackUrl && !this.state.usingFallback) {
            // Do not raise isLoading here: the swapped-in element is what reports load state,
            // and gating it behind a spinner would stop it ever mounting.
            this.setState({ usingFallback: true, error: null });
            return;
        }
        this.setState({ error: reason, isLoading: false });
    };

    /**
     * A video can reach metadata with no decodable picture — AV1 on Safari below an M3, HEVC
     * with no hardware decoder. It never fires `error`; it just paints nothing. Zero
     * `videoWidth` is how that shows up, and it is the difference between the user seeing a
     * poster and the user seeing a black rectangle and concluding we broke their file.
     */
    private onVideoMetadata = (e: any) => {
        const el = e?.currentTarget;
        if (el && el.videoWidth === 0 && el.videoHeight === 0) {
            this.failOver('This browser can\'t play this video format.');
            return;
        }
        this.setState({ isLoading: false });
    };

    private renderContent() {
        const { mediaType } = this.props;
        const url = this.activeUrl;
        const { isLoading, error, textContent } = this.state;

        // Only pdf and text have no element of their own to report progress, so only they can
        // stand behind a bare spinner. For image and video the element IS the loading signal --
        // returning early here means it never mounts, nothing ever fires onLoad or onError, and
        // the modal sits on "Loading..." forever. That is exactly what happened when failOver
        // set isLoading while swapping in the poster.
        if (isLoading && (mediaType === 'pdf' || mediaType === 'text')) {
            return (
                <div className={styles.loader}>
                    <div className={styles.spinner}></div>
                    <span>Loading...</span>
                </div>
            );
        }

        if (error) {
            return (
                <div className={styles.error}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>{error}</span>
                </div>
            );
        }

        switch (mediaType) {
            case 'image':
                return (
                    <div className={styles.imageContainer}>
                        <img
                            src={url}
                            alt={this.props.filename || 'Preview'}
                            className={styles.image}
                            onLoad={(e: any) => {
                                // A decoder that gives up mid-file can still fire load with no
                                // pixels; treat that as a failure rather than showing nothing.
                                if (e?.currentTarget?.naturalWidth === 0) {
                                    this.failOver('This browser can\'t display this image format.');
                                    return;
                                }
                                this.setState({ isLoading: false });
                            }}
                            onError={() => this.failOver('This browser can\'t display this image format.')}
                        />
                    </div>
                );

            case 'video':
                if (this.state.usingFallback) {
                    // The poster is a still image; keeping a <video> element here would show
                    // controls that do nothing.
                    return (
                        <div className={styles.imageContainer}>
                            <img
                                src={url}
                                alt={this.props.filename || 'Preview'}
                                className={styles.image}
                                onLoad={() => this.setState({ isLoading: false })}
                                onError={() => this.setState({ error: 'Preview unavailable for this file.', isLoading: false })}
                            />
                        </div>
                    );
                }
                if (!this.state.useNativePlayer) {
                    // Enhanced player host: video.js mounts here once its chunk arrives
                    // (maybeInitVjs). Until then the modal spinner covers the wait.
                    return (
                        <div className={styles.videoContainer}>
                            {/* Outer div owns our sizing class; video.js adopts the inner
                                data-vjs-player div as its shell and OVERWRITES its className,
                                so nothing we need may live on that element. */}
                            <div className={styles.vjsHost}>
                                <div data-vjs-player ref={this.vjsHostRef} />
                            </div>
                        </div>
                    );
                }
                return (
                    <div className={styles.videoContainer}>
                        <video
                            src={url}
                            className={styles.video}
                            controls
                            autoPlay={false}
                            onLoadedMetadata={this.onVideoMetadata}
                            onError={() => this.failOver('This browser can\'t play this video format.')}
                        />
                    </div>
                );

            case 'audio':
                if (!this.state.useNativePlayer) {
                    return (
                        <div className={styles.audioContainer}>
                            <div className={styles.audioVisual}>
                                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                                    <path d="M9 18V5l12-2v13" />
                                    <circle cx="6" cy="18" r="3" />
                                    <circle cx="18" cy="16" r="3" />
                                </svg>
                            </div>
                            <div className={styles.vjsAudioHost}>
                                <div data-vjs-player ref={this.vjsHostRef} />
                            </div>
                        </div>
                    );
                }
                return (
                    <div className={styles.audioContainer}>
                        <div className={styles.audioVisual}>
                            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                                <path d="M9 18V5l12-2v13" />
                                <circle cx="6" cy="18" r="3" />
                                <circle cx="18" cy="16" r="3" />
                            </svg>
                        </div>
                        <audio
                            src={url}
                            className={styles.audio}
                            controls
                            onLoadedData={() => this.setState({ isLoading: false })}
                            onError={() => this.setState({ error: 'Failed to load audio' })}
                        />
                    </div>
                );

            case 'pdf':
                return (
                    <div className={styles.pdfContainer}>
                        <canvas ref={this.canvasRef} className={styles.pdfCanvas} />
                    </div>
                );

            case 'text':
                return (
                    <div className={styles.textContainer}>
                        <pre className={styles.textContent}>{textContent}</pre>
                    </div>
                );

            default:
                return (
                    <div className={styles.unsupported}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                        </svg>
                        <span>Preview not available for this file type</span>
                    </div>
                );
        }
    }

    render() {
        const { mediaType, filename } = this.props;
        const { currentPage, totalPages, isEditingPage, pageInputValue } = this.state;

        return (
            <div className={styles.overlay} onClick={this.close}>
                <div
                    className={styles.modal}
                    ref={this.containerRef}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className={styles.header}>
                        <div className={styles.headerLeft}>
                            {filename && <span className={styles.filename}>{filename}</span>}
                        </div>
                        <div className={styles.headerRight}>
                            <button
                                type="button"
                                className={styles.headerBtn}
                                title="Download"
                                onClick={this.handleDownload}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                            </button>
                            <button type="button" className={styles.closeBtn} onClick={this.close} title="Close (Esc)">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className={styles.content} ref={this.contentRef}>
                        {this.activeNotice && (
                            <p className={styles.notice} role="status">{this.activeNotice}</p>
                        )}
                        {this.renderContent()}
                    </div>

                    {/* Footer - PDF pagination */}
                    {mediaType === 'pdf' && totalPages > 0 && (
                        <div className={styles.footer}>
                            <button
                                className={styles.pageBtn}
                                onClick={this.prevPage}
                                disabled={currentPage <= 1}
                                title="Previous page (←)"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="15 18 9 12 15 6" />
                                </svg>
                            </button>
                            {isEditingPage ? (
                                <div className={styles.pageInputContainer}>
                                    <input
                                        ref={this.pageInputRef}
                                        type="number"
                                        className={styles.pageInput}
                                        value={pageInputValue}
                                        onChange={this.handlePageInputChange}
                                        onKeyDown={this.handlePageInputKeyDown}
                                        onBlur={this.handlePageInputBlur}
                                        min={1}
                                        max={totalPages}
                                    />
                                    <span className={styles.pageDivider}>/ {totalPages}</span>
                                </div>
                            ) : (
                                <span className={styles.pageInfo} onClick={this.handlePageClick} title="Click to go to page">
                                    {currentPage} / {totalPages}
                                </span>
                            )}
                            <button
                                className={styles.pageBtn}
                                onClick={this.nextPage}
                                disabled={currentPage >= totalPages}
                                title="Next page (→)"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default MediaPreviewModalComponent;
